import moment from "moment";
import React, { useContext, useEffect, useRef, useState } from "react";
import {
	Badge,
	Button,
	Dropdown,
	Form,
	Table,
} from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { BiDotsHorizontalRounded, BiSearch, BiSort, BiPlus, BiPencil, BiBookOpen, BiUpload, BiTrash } from "react-icons/bi";
import { CgSortAz } from "react-icons/cg";
import { FcFinePrint } from "react-icons/fc";
import { Link, useSearchParams } from "react-router-dom";
import { LoaderContext, RegulationContext } from "../../../App";
import PaginationComponent from "../../../components/Pagination/Pagination";
import { DEFAULT_ITEMS_PER_PAGE, FORM_BUILDER_LISTING_URL, LIST_DEF_SORT, LS_ACCESS_TOKEN_KEY, LS_AUTHORITY_KEY, SORT_ORDER_DESC, STATUS } from "../../../core/constants/common";
import FormService from "../../../core/service/FormService";
import Utils from "../../../core/utilities/Utils";
import "./Forms.scss";
import NewFormModal from "./NewFormModal";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import { toast } from "react-toastify";
import useHasPermission from "../../../core/hooks/useHasPermission";


const DEFAULT_SORT_FIELD = 'lastModifiedDate';

const parsePageFromParams = (searchParams) => {
	const page = parseInt(searchParams.get('page'), 10);
	return page > 0 ? page : 1;
};

const parseSizeFromParams = (searchParams) => {
	const size = parseInt(searchParams.get('size'), 10);
	return size > 0 ? size : DEFAULT_ITEMS_PER_PAGE;
};

function FormList({ t }) {
	const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
	const authToken = localStorage.getItem(LS_ACCESS_TOKEN_KEY);
	const currentRoute = window.location.pathname;
	const [searchParams, setSearchParams] = useSearchParams();
	const isUpdatingUrlRef = useRef(false);

	const [editedForm, setEditedForm] = useState({ id: -1 });
	const [itemsTotalCount, setItemTotalCount] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(() => parseSizeFromParams(searchParams));
	const [currentPage, setCurrentPage] = useState(() => parsePageFromParams(searchParams));
	const [order, setOrder] = useState(() => searchParams.get('order') ?? SORT_ORDER_DESC);
	const [sortField, setSortField] = useState(() => searchParams.get('sort') ?? DEFAULT_SORT_FIELD);
	const [searchKey, setSearchKey] = useState(() => searchParams.get('search') ?? "");
	const [forms, setForms] = useState([]);
	const { glbRegulation, setGlbRegulation } = useContext(RegulationContext);
	const { setLoaderDisplay } = useContext(LoaderContext);
	const [selfAssessment, setSelfAssessment] = useState(true);
	const [selectedFormId, setSelectedFormId] = useState(0);
	const [formIdToDelete, setFormIdToDelete] = useState(0);

	const glbRegulationId = glbRegulation?.id ? glbRegulation.id : "";
	const { hasSettingsUpdatePermission } = useHasPermission();

	const getListStateQueryParams = (state = {}) => {
		const page = state.page ?? currentPage;
		const size = state.size ?? itemsPerPage;
		const search = state.search ?? searchKey;
		const sort = state.sort ?? sortField;
		const orderVal = state.orderVal ?? order;
		const qParams = {};

		if (page > 1) {
			qParams.page = String(page);
		}
		if (size !== DEFAULT_ITEMS_PER_PAGE) {
			qParams.size = String(size);
		}
		if (search) {
			qParams.search = search;
		}
		if (sort !== DEFAULT_SORT_FIELD) {
			qParams.sort = sort;
		}
		if (orderVal !== SORT_ORDER_DESC) {
			qParams.order = orderVal;
		}

		return qParams;
	};

	const updateListUrlParams = (state = {}) => {
		const qParams = getListStateQueryParams(state);
		const newSearchParams = new URLSearchParams();

		Object.entries(qParams).forEach(([key, value]) => {
			newSearchParams.set(key, value);
		});

		isUpdatingUrlRef.current = true;
		setSearchParams(newSearchParams, { replace: true });
	};

	const getFormNavigationParams = (state = {}) => {
		const qParams = getListStateQueryParams(state);
		return Object.keys(qParams).length > 0
			? { route: currentRoute, qParams }
			: { route: currentRoute };
	};

	useEffect(() => {
		if (isUpdatingUrlRef.current) {
			isUpdatingUrlRef.current = false;
			return;
		}

		const page = parsePageFromParams(searchParams);
		const size = parseSizeFromParams(searchParams);
		const search = searchParams.get('search') ?? "";
		const sort = searchParams.get('sort') ?? DEFAULT_SORT_FIELD;
		const orderVal = searchParams.get('order') ?? SORT_ORDER_DESC;

		setCurrentPage(page);
		setItemsPerPage(size);
		setSearchKey(search);
		setSortField(sort);
		setOrder(orderVal);
		getForms(page, size, search, sort, orderVal);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [glbRegulationId, searchParams]);

	const getForms = (
		pageNumber,
		itemsPerPageVal,
		searchVal = "",
		sortField,
		orderVal,
		isSelf = selfAssessment
	) => {
		if (pageNumber > 0) {
			pageNumber = pageNumber - 1;
		} else {
			setCurrentPage(1);
		}
		const perPage = itemsPerPageVal > 0 ? itemsPerPageVal : itemsPerPage;
		const sort = sortField + "," + orderVal;
		FormService.getForms(
			pageNumber,
			perPage,
			searchVal,
			sort,
			glbRegulationId,
			isSelf
		).then((response) => {
			setItemTotalCount(parseFloat(response.headers["x-total-count"]));
			setForms([...response.data]);
		})
			.catch((error) => { });
	};

	const handleSearch = (key) => {
		setSearchKey(key);
		setCurrentPage(1);
		getForms(0, itemsPerPage, key, sortField, order);
		updateListUrlParams({ page: 1, search: key });
	};

	const handleSort = (val) => {
		let orderval = null;
		if (sortField === val && order === "asc") {
			orderval = "desc";
		} else {
			orderval = "asc";
		}
		setOrder(orderval);
		setSortField(val);
		setCurrentPage(1);
		getForms(0, itemsPerPage, searchKey, val, orderval);
		updateListUrlParams({ page: 1, sort: val, orderVal: orderval });
	};

	const handleForm = (action, form) => {
		const formType = "ASSESSMENT";
		const hasPermission = hasSettingsUpdatePermission();
		const regId = form?.regulation?.id ?? '';
		if (action === "NEW") {
		} else if (action === "EDIT") {
			const str = Utils.generateUrlParamFormCreate(
				action,
				authToken,
				formType,
				regId,
				form?.id,
				'',
				'',
				hasPermission,
				getFormNavigationParams()
			);
			const encodedDat = Utils.encodeBase64(str);
			window.location.href = FORM_BUILDER_LISTING_URL + `/?q=${encodedDat}`;
		}
	};

	const callbackNewForm = (form, val) => {
		setEditedForm({ id: -1 });
		if (!val) return;

		const str = Utils.generateUrlParamFormCreate(
			"EDIT",
			authToken,
			"ASSESSMENT",
			form?.regulation?.id,
			form?.id,
			"",
			"",
			getFormNavigationParams()
		);
		const encodedDat = Utils.encodeBase64(str);
		const url = FORM_BUILDER_LISTING_URL + `/?q=${encodedDat}`;
		if (!url) return;

		window.location.href = url;
	};

	const handleAssessmentType = (e) => {
		const val = e.target.value;
		setSelfAssessment(val === "true" ? true : val === "false" && false);
		setCurrentPage(1);
		getForms(0, itemsPerPage, searchKey, sortField, order, val);
		updateListUrlParams({ page: 1 });
	};

	const handleFormPublish = (publish) => {
		if (!publish) {
			setSelectedFormId(0);
			return;
		}

		FormService.publishForms(selectedFormId)
			.then((response) => {
				if (response.status !== 200) return;
				toast.success("Success", {
					position: toast.POSITION.TOP_RIGHT,
				});
				getForms(currentPage, itemsPerPage, searchKey, sortField, order);
			})
			.catch((error) => { });
		setSelectedFormId(0);
	};

	const handleFormDelete = (confirmed) => {
		if (!confirmed) {
			setFormIdToDelete(0);
			return;
		}

		setLoaderDisplay(true);
		FormService.deleteForm(formIdToDelete)
			.then((response) => {
				setLoaderDisplay(false);
				if (response.status !== 200 && response.status !== 204) return;
				toast.success("Success", {
					position: toast.POSITION.TOP_RIGHT,
				});
				getForms(currentPage, itemsPerPage, searchKey, sortField, order);
			})
			.catch((error) => {
				setLoaderDisplay(false);
			});
		setFormIdToDelete(0);
	};

	const headerToRender = () => {
		return (
			<div className="List_header">
				<h5 className="MH">{t("Forms.label")}</h5>
				<div className="List_sorts_menu">
					<Button variant="primary" hidden={!hasSettingsUpdatePermission()} onClick={() => setEditedForm({ id: 0 })}>
						<BiPlus className="me-1" />
						{t("New.label")}
					</Button>
				</div>
			</div>
		);
	};

	const headerActionsToRender = () => {
		return (
			<div className="List_table-card01_head">
				<div className="List_table-card_Search_01">
					<div className="List_table-card_Search_Box">
						<Form>
							<input
								type="text"
								className="form-control"
								placeholder={t("SearchByName.label")}
								value={searchKey}
								onChange={(e) => handleSearch(e.target.value)} />
							<a className="Search_iocnBox cursor"
								onClick={() => handleSearch(searchKey)}>
								<BiSearch />
							</a>
						</Form>
					</div>
				</div>

				{/* <div className="List_table-card01_sort">
					<ul>
						<li>
							<div className="List_table-card_Assessment">
								<Form.Check
									inline
									label={t("company.label")}
									name="assessType"
									type="radio"
									id="inline-radio-2"
									value={true}
									checked={selfAssessment === true ? "checked" : ""}
									onChange={(e) => handleAssessmentType(e)} />
								<Form.Check
									inline
									label={t("Supplier.label")}
									name="assessType"
									type="radio"
									id="inline-radio-1"
									value={false}
									checked={selfAssessment === false ? "checked" : ""}
									onChange={(e) => handleAssessmentType(e)} />
							</div>
						</li> */}
				{/* required Later */}
				{/* <li className="filter">
							<Link to="#">
								<button className="btn btn-secondary"><CgSortAz /> {t("Filter.label")}</button>
							</Link>
						</li> */}
				{/* </ul>
				</div> */}
			</div>
		);
	};

	const renderTableHEader = () => {
		return (
			<thead>
				<tr>
					<th>
						<Link to="#" onClick={() => handleSort("name")}>
							{t("Name.label")}
							<BiSort />
						</Link>
					</th>
					<th>{t("Description.label")}</th>
					<th>
						<Link to="#" onClick={() => handleSort("lastModifiedDate")}>
							{t("LastModified.label")}
							<BiSort />
						</Link>
					</th>
					<th>
						<Link to="#" onClick={() => handleSort("status")}>
							{t("Status.label")}
							<BiSort />
						</Link>
					</th>
					<th></th>
				</tr>
			</thead>
		);
	}

	const renderStatus = (form) => {
		switch (form.status) {
			case "DRAFT":
				return (
					<Badge bg="badge-Draft">
						{STATUS[form.status]}
					</Badge>
				);
			case "PUBLISHED":
				return (
					<Badge bg="badge-Published">
						{STATUS[form.status]}
					</Badge>
				);
		}
	};

	const renderFormOptions = (form) => {
		return (
			<Dropdown>
				<Dropdown.Toggle
					variant="blank_outline"
					id="dropdown-basic">
					<BiDotsHorizontalRounded />
				</Dropdown.Toggle>
				<Dropdown.Menu>
					<Dropdown.Item
						onClick={() => setEditedForm(form)} hidden={!hasSettingsUpdatePermission()}>
						<BiPencil className="me-2" />
						{t("Edit.label")}
					</Dropdown.Item>
					<Dropdown.Item
						onClick={() => handleForm("EDIT", form)}>
						<BiBookOpen  className="me-2" />
						{t("Open.label")}
					</Dropdown.Item>
					{
						form.status !== "PUBLISHED" &&
						<Dropdown.Item
							onClick={() => setSelectedFormId(form.id)} hidden={!hasSettingsUpdatePermission()}>
							<BiUpload className="me-2" />
							{t("Publish.label")}
						</Dropdown.Item>
					}
					<Dropdown.Item
						onClick={() => setFormIdToDelete(form.id)}
						hidden={!hasSettingsUpdatePermission()}>
						<BiTrash className="me-2" />
						{t("Delete.label")}
					</Dropdown.Item>
				</Dropdown.Menu>
			</Dropdown>
		);
	};

	const renderDataApprovalToggle = (form) => {
		return (
			<span className="cursor">
				<Form.Check
					type="switch"
					label={form.reviewRequired ? t('Yes.label') : t('No.label')}
					key={`dataApprovalCheck-${form.id}`}
					id={`dataApprovalCheck-${form.id}`}
					variant="outline-primary"
					checked={form.reviewRequired}
					onChange={() => enableDataApproval(form, !form.reviewRequired)}
				/>
			</span>
		);
	};

	const enableDataApproval = (form, status) => {
		FormService.updateDataApproval(status, form.id).then(res => {
			if (!res.status) return;

			toast.success("Success", {
				position: toast.POSITION.TOP_RIGHT,
			});
			getForms(currentPage, itemsPerPage, searchKey, sortField, order);
		}).catch(error => { });
	};

	return (
		<>
			<div className="FormsList">
				{headerToRender()}
				<div className="List_Table_01 mt-2">
					{headerActionsToRender()}
					<div className="AssessmentTable_card">
						<div className="card List_table-card01 ">
							<Table>
								{renderTableHEader()}
								{
									forms?.length > 0
										?
										forms?.map((form, index) => (
											<tbody>
												<tr key={`form-row-${index + 1}`}>
													<td>{form.name}</td>
													<td>{form?.description}</td>
													<td>{moment(form?.lastModifiedDate).format("MMM DD, YYYY")}</td>
													<td>{renderStatus(form)}</td>
													<td>{renderFormOptions(form)}</td>
												</tr>
											</tbody>
										))
										:
										<tbody>
											<tr>
												<td colSpan="7" className="text-center">
													<div className="Nodatafound_box">
														<h6><FcFinePrint className="me-2" /> {t("Nodatafound.label")}</h6>
													</div>
												</td>
											</tr>
										</tbody>
								}
							</Table>
							<div className="p-2 List_footer_count">
								<PaginationComponent
									itemsTotalCount={itemsTotalCount}
									itemsPerPage={itemsPerPage}
									currentPage={currentPage}
									setCurrentPage={setCurrentPage}
									alwaysShown={true}
									setItemsPerPage={setItemsPerPage}
									pageClicked={(pageNum, perPage) => {
										const page = pageNum > 0 ? pageNum : 1;
										const size = perPage > 0 ? perPage : itemsPerPage;
										getForms(pageNum, perPage, searchKey, sortField, order);
										updateListUrlParams({ page, size });
									}}
								/>
							</div>
						</div>
					</div>
				</div>
			</div >
			{
				editedForm?.id >= 0 &&
				<NewFormModal
					showModal={true}
					formToUpdate={editedForm}
					regulation={glbRegulationId}
					assessmentType={selfAssessment ? 'SELFASSESSMENT' : 'SUPPLIERASSESSMENT'}
					handleNewForm={callbackNewForm}
					onFormSave={() => {
						setEditedForm({ id: -1 });
						getForms(currentPage, itemsPerPage, searchKey, sortField, order);
					}} />
			}
			{
				selectedFormId > 0 && (
					<ConfirmationModal
						modalShow={true}
						messageText={t("PublishConfirmationMsg.label")}
						callbackModal={handleFormPublish} />
				)}
			{
				formIdToDelete > 0 && (
					<ConfirmationModal
						modalShow={true}
						messageText={t("FormList.deleteConfirmation.label")}
						callbackModal={handleFormDelete} />
				)}
		</>
	);
}
export default withTranslation()(FormList);
